/**
 * User-facing copy for the public approvals demo (SKE-4166).
 *
 * The app has no i18n runtime today, so rather than invent one for a single
 * feature every demo string is centralised here behind `demoText()`. Wiring a
 * real translation layer later is then a one-file change — swap the lookup for
 * `t('demo.<key>')` and no component has to be touched. English only for now.
 *
 * Strings carrying `{placeholders}` are rendered through `demoFormat()`, which
 * is the substitution shape every i18n runtime already speaks, so they migrate
 * without a rewrite.
 */

export const demoStrings = {
  resetLabel: 'Reset demo',
  resetAriaLabel: 'Reset the demo back to its starting state',
  /** Name shown against actions the visitor takes. */
  reviewerName: 'You',
  /** Note seeded on the curated changes-requested post. */
  seededRequestChangeNote:
    'Can we swap the opening line for something punchier? The rest looks great.',

  /* ---------------------------------------------------------------------- */
  /* Honesty framing — "this is a demo" (Phase 7)                            */
  /* ---------------------------------------------------------------------- */

  /**
   * Non-negotiable copy. The demo runs the REAL approver UI on REAL media, and
   * once personalized it wears a real company's name and logo — so the two
   * questions a visitor could otherwise get wrong ("is this my client's actual
   * tool?" / "did I just publish something?") are answered on screen at all
   * times, on every viewport, whether or not the board has been re-skinned.
   */
  honestyBadgeLabel: 'Interactive demo · sample brand',
  /** Personalized: name the borrowing explicitly rather than dropping it. */
  honestyBadgeBrandedLabel: 'Interactive demo · styled as {brand}',
  honestyBadgeAriaLabel: 'About this demo',

  honestyNote: 'Sample posts, fictional agency — nothing here is published.',
  /**
   * `{brand}` is the visitor's own client. Says the two things a re-skinned
   * board must never leave ambiguous: whose software this is, and that the
   * branding is borrowed for illustration.
   */
  honestyNoteBranded:
    'A Sked Social demo wearing {brand}’s branding — nothing here is published.',

  /* ---------------------------------------------------------------------- */
  /* Marketer mirror — "what your team sees" (Phase 2)                       */
  /* ---------------------------------------------------------------------- */

  mirrorPanelTitle: 'Your team’s view',
  mirrorPanelSubtitle: 'Updates in Sked the moment you act',
  mirrorWorkspaceName: 'Driftline Studio',
  mirrorWorkspaceMeta: 'Content calendar',
  mirrorAriaLabel: 'What your team sees in Sked',
  mirrorProgressLabel: '{done} of {total} reviewed',
  mirrorAwaitingLabel: '{count} still awaiting your client',
  mirrorAllClearLabel: 'Queue cleared — nothing left to chase',
  mirrorEmpty: 'Nothing scheduled yet.',
  mirrorActivityTitle: 'Activity',
  mirrorActivityEmpty:
    'Approve or reject a post and your team’s side updates here.',
  mirrorJustNow: 'just now',

  mirrorStatusPending: 'Awaiting approval',
  mirrorStatusApproved: 'Approved',
  mirrorStatusRequestChange: 'Changes requested',

  /** Toast + activity-feed lines. `{brand}` is the client account. */
  mirrorNoticeApproved: '{brand} approved “{caption}”',
  mirrorNoticeRequestChange: '{brand} requested changes on “{caption}”',
  mirrorNoticePending: '{brand} reopened “{caption}”',
  mirrorNoticeComment: '{brand} left a note on “{caption}”',

  /* ---------------------------------------------------------------------- */
  /* Role switching                                                          */
  /* ---------------------------------------------------------------------- */

  roleToggleAriaLabel: 'Switch between the client view and your team’s view',
  roleClientLabel: 'View as client',
  roleTeamLabel: 'View as your team',
  roleClientShortLabel: 'Client',
  roleTeamShortLabel: 'Your team',
  mirrorCollapseLabel: 'Hide your team’s view',
  mirrorExpandLabel: 'Show your team’s view',

  /* ---------------------------------------------------------------------- */
  /* Persistent side identity + intro tour + approve hint (SKE-4234)         */
  /* ---------------------------------------------------------------------- */

  /**
   * The two sides are colour-coded for the whole session — client = warm
   * (spice), team = blurple — so the split reads even without the tour. These
   * labels live in each frame's chrome bar (desktop) and the role toggle
   * (mobile).
   */
  sideClientLabel: 'Client’s link',
  sideTeamLabel: 'Your workspace · internal',
  /** Shorter forms for the mobile role toggle. */
  sideClientMobileLabel: 'Client’s link',
  sideTeamMobileLabel: 'Your workspace',

  /** The spotlight pill that names the side each tour step is about. */
  tourClientPill: 'CLIENT',
  tourTeamPill: 'YOUR TEAM',
  /** Action-first captions — the pill is the label, so no eyebrow. */
  tourClientCaption: 'Your client approves here.',
  tourTeamCaption: 'You see it here — live.',
  /** One supporting sentence under each caption — the "why it matters" beat. */
  tourClientBody:
    'Send one link — no login, no account — and they approve or request changes right in the browser.',
  tourTeamBody:
    'Every decision lands in your Sked calendar the moment it happens — nothing to chase or copy across.',

  /** The lingering "do this next" cue that points at the Approve button. */
  approveHintLabel: 'Approve a post ↓',

  /* ---------------------------------------------------------------------- */
  /* Email unlock — the soft gate (Phase 3)                                  */
  /* ---------------------------------------------------------------------- */

  /** Persistent, unobtrusive control that reopens the gate. */
  gateOpenLabel: 'Personalize this demo',
  gateOpenAriaLabel: 'Personalize this demo with your own client',

  gateTitle: 'Make this board yours',
  gateBody:
    'You’ve seen how approvals feel. Add your email and we’ll shape this board around the clients you actually work with — no account, no card.',
  gateEmailLabel: 'Work email',
  gateEmailPlaceholder: 'you@agency.com',
  gateSubmitLabel: 'Personalize my board',
  gateSubmittingLabel: 'Setting things up…',
  gateDismissLabel: 'Keep exploring',
  gateFootnote: 'One email about Sked. Unsubscribe any time.',

  gateErrorInvalidEmail: 'That doesn’t look like an email address.',
  gateErrorCaptcha: 'Captcha verification failed. Please try again.',
  gateErrorRateLimited: 'Too many attempts. Give it a minute and try again.',
  gateErrorNetwork: 'We couldn’t reach us just then. Please try again.',
  gateErrorServer: 'Something went wrong on our side. Please try again.',

  /* Unlocked state + the Phase-4 personalization placeholder. */
  gateUnlockedTitle: 'You’re in',
  gateUnlockedBody:
    'Keep approving — your personalized board is on its way to {domain}.',
  gateUnlockedGenericBody:
    'Keep approving — your personalized board is on its way.',
  gateUnlockedBadge: 'Personalized',

  /* ---------------------------------------------------------------------- */
  /* Personalization — re-skin the board to the visitor's client (Phase 4)   */
  /* ---------------------------------------------------------------------- */

  brandPanelTitle: 'Make it your client’s',
  brandPanelBody: 'Add a client and this board wears their brand.',
  brandInputLabel: 'Client website or @handle',
  brandInputPlaceholder: 'marigold.com or @marigoldrye',
  brandSubmitLabel: 'Apply their brand',
  brandSubmittingLabel: 'Looking them up…',
  brandChangeLabel: 'Try another client',
  brandCancelLabel: 'Cancel',

  /** Shown once a brand is applied. `{brand}` is the client's name. */
  brandAppliedTitle: 'This is {brand}’s board',
  brandAppliedBody: 'Every approval below is the one your client would give.',
  brandAppliedBadge: 'Personalized',
  /** Honest about a name-only match, without calling it a failure. */
  brandDerivedHint: 'We couldn’t find their logo, so we made one.',

  brandErrorInvalid:
    'That doesn’t look like a website or a handle. Try marigold.com.',
  brandErrorBlocked: 'Pick one of your own clients and we’ll skin the board.',
  brandErrorRateLimited: 'That’s a lot of clients. Give it a minute.',

  /* ---------------------------------------------------------------------- */
  /* AI marketer reply — the thread answers back (Phase 5)                   */
  /* ---------------------------------------------------------------------- */

  /**
   * Who replies. A named person on the team behind `mirrorWorkspaceName`, not
   * the studio itself — a thread where a person answers reads like a working
   * relationship, which is the thing the demo is selling.
   */
  aiReplyAuthorName: 'Ana Reyes',
  /**
   * The typing beat. Shown for a moment before the reply lands, and skipped
   * entirely under `prefers-reduced-motion` (see useDemoAiReply).
   */
  aiReplyTyping: '{name} is typing…',

  /**
   * Canned replies for when the endpoint is unreachable, unconfigured, or
   * rate-limited. They mirror the server's own set (see
   * `services/demoAiReplyPrompt.ts`) so a visitor cannot tell which side
   * produced the line — each is something a marketer could truthfully say to
   * any note on any post.
   */
  aiReplyFallbackOne:
    'Got it — thanks for taking a look. I’ll pick this one up and get a revised version back to you shortly.',
  aiReplyFallbackTwo:
    'Thanks for the note! Leave it with me and I’ll get that sorted before this one goes out.',
  aiReplyFallbackThree:
    'Appreciate the quick feedback. I’ll make the change and pop it back in here for you to check.',
  aiReplyFallbackFour:
    'Noted, thank you — I’ll work through this and let you know as soon as it’s updated.',
  aiReplyFallbackFive:
    'Perfect, that’s really helpful. I’ll get this tidied up and back in front of you today.',

  /* ---------------------------------------------------------------------- */
  /* End-state CTAs — the conversion moment (Phase 6)                        */
  /* ---------------------------------------------------------------------- */

  /**
   * Two headlines, because the panel surfaces at two different beats. CLEARED
   * is the payoff — the visitor just finished a round. ENGAGED is the standing
   * offer they open from the pill mid-play; the intro tour has already taught
   * what the two sides are, so this stays a short next-step, NOT an explainer.
   */
  ctaClearedTitle: 'That’s the whole approval round, done.',
  ctaEngagedTitle: 'Run this with your own clients.',
  /** `{approved}` / `{changes}` come straight from the visitor's own board. */
  ctaClearedBody:
    'You approved {approved} and sent {changes} back — no logins, no chasing, no spreadsheet. Your team saw every decision the moment you made it.',
  ctaEngagedBody:
    'Start a free trial, book a quick walkthrough, or have the recap emailed to you.',

  ctaOpenLabel: 'What happens next',
  ctaOpenAriaLabel: 'See how to run this with your own clients',
  ctaDismissLabel: 'Keep exploring',
  ctaAriaLabel: 'Ways to take the Sked approvals demo further',

  ctaTrialLabel: 'Start free trial',
  ctaTrialHint: 'No credit card required.',
  ctaBookLabel: 'Book a demo call',
  ctaBookHint: 'Fifteen minutes with someone who does this all day.',
  ctaSummaryLabel: 'Email me a summary',
  ctaSummaryHint: 'The recap, the demo link, and what it costs.',

  /* The summary form, shown when the visitor picks "email me a summary". */
  ctaSummaryTitle: 'Where should we send it?',
  ctaSummaryBody:
    'We’ll email you the recap of this board, a link to pick it back up, and where to go from here.',
  ctaSummaryEmailLabel: 'Work email',
  ctaSummaryEmailPlaceholder: 'you@agency.com',
  ctaSummarySubmitLabel: 'Send my recap',
  ctaSummarySubmittingLabel: 'Sending…',
  ctaSummaryBackLabel: 'Back',
  ctaSummarySentTitle: 'On its way',
  /** `{domain}` is the visitor's own email domain. */
  ctaSummarySentBody: 'Check your {domain} inbox in a minute or two.',
  ctaSummarySentGenericBody: 'Check your inbox in a minute or two.',

  ctaSummaryErrorInvalidEmail: 'That doesn’t look like an email address.',
  ctaSummaryErrorCaptcha: 'Captcha verification failed. Please try again.',
  ctaSummaryErrorRateLimited:
    'That’s a few recaps already. Give it a minute and try again.',
  ctaSummaryErrorNetwork: 'We couldn’t reach us just then. Please try again.',
  ctaSummaryErrorServer: 'Something went wrong on our side. Please try again.',
} as const

export type DemoStringKey = keyof typeof demoStrings

export const demoText = (key: DemoStringKey): string => demoStrings[key]

/**
 * Substitute `{name}` placeholders. Unknown placeholders are left verbatim so a
 * copy edit that drops a variable degrades to visible text rather than to the
 * string "undefined".
 */
export const demoFormat = (
  key: DemoStringKey,
  values: Record<string, string | number>
): string =>
  demoStrings[key].replace(/\{(\w+)\}/g, (match, name: string) =>
    name in values ? String(values[name]) : match
  )
